<template>
  <div class="blog-detail-page">
    <section class="hero" :class="heroTheme.className" :style="heroTheme.styleVars" v-if="article">
      <div class="container">
        <div class="hero-kicker">
          <span class="hero-date">{{ article.meta.date }}</span>
          <span class="hero-dot" />
          <span class="hero-reading-time">{{ readingTimeLabel }}</span>
        </div>
        <h1 class="hero-title">{{ article.meta.title }}</h1>
        <p class="hero-description">{{ article.meta.description }}</p>
        <div class="hero-tags" v-if="article.meta.tags?.length">
          <span v-for="tag in article.meta.tags" :key="tag" class="hero-tag">{{ tag }}</span>
        </div>
      </div>
    </section>
    <div class="container">
      <BlogBreadcrumb :article-title="article?.meta?.title" />
    </div>

    <section class="article-area" v-if="article">
      <div class="article-container">
        <MDC
          v-slot="{ body, data: markdownData, toc }"
          :value="articleBody"
          :partial="false"
          :parser-options="markdownParserOptions"
        >
          <div class="article-layout" :class="{ 'has-toc': toc?.links?.length }">
            <BlogTableOfContents :items="toc?.links ?? []" :title="tocTitle" />

            <div class="article-main">
              <article class="article-shell body">
                <MDCRenderer :body="body" :data="markdownData" tag="div" />
                <BlogPrimaryCta v-if="primaryCta" :cta="primaryCta" />
              </article>
            </div>
          </div>
        </MDC>
      </div>
    </section>
  </div>
</template>

<script setup>
import BlogPrimaryCta from '~/components/blog/BlogPrimaryCta.vue';
import BlogBreadcrumb from '~/components/blog/BlogBreadcrumb.vue';
import BlogTableOfContents from '~/components/blog/BlogTableOfContents.vue';
import { resolveBlogDetailFetchState } from '~/utils/blogDetailPageState';
import { prepareBlogArticleMarkdown } from '~/utils/blogContentRender';
import { estimateReadingMinutes, formatReadingTime } from '~/utils/blogReadingTime';
import { resolveBlogHeroTheme } from '~/utils/blogHeroTheme';
import { canonicalizeUrl, ensureValidSiteUrl } from '~/utils/websiteSeo';

const route = useRoute();
const { locale, t } = useI18n();
const runtimeConfig = useRuntimeConfig();
const markdownParserOptions = {
  toc: { depth: 1 },
  contentHeading: false
};

const currentLocale = computed(() => (locale.value === 'en' ? 'en' : 'zh'));
const slug = computed(() => String(route.params.slug ?? '').trim());

const { data, error } = await useFetch(() => `/api/blog/${slug.value}`, {
  query: { locale: currentLocale }
});

const pageState = resolveBlogDetailFetchState({
  data: data.value,
  error: error.value
});
if (pageState.shouldThrow) {
  throw createError(pageState.throwError);
}

const article = computed(() => pageState.article);
const primaryCta = computed(() => pageState.primaryCta);
const availableLocales = computed(() => pageState.availableLocales ?? [currentLocale.value]);
const articleBody = computed(() => prepareBlogArticleMarkdown(article.value?.body ?? ''));
const tocTitle = computed(() => t('blog.detail.tocTitle'));
const heroTheme = computed(() => resolveBlogHeroTheme(article.value));
const readingTimeLabel = computed(() =>
  formatReadingTime(
    estimateReadingMinutes({
      body: articleBody.value,
      locale: currentLocale.value
    }),
    currentLocale.value
  )
);
const siteUrl = ensureValidSiteUrl(runtimeConfig.public.siteUrl);
const canonicalUrl = computed(() => canonicalizeUrl({ siteUrl, path: route.path }));

useWebsiteSeo({
  title: () => article.value?.meta?.title ?? 'Blog | OLTranslation',
  description: () => article.value?.meta?.description ?? 'Blog article',
  forceNoIndex: pageState.forceNoIndex,
  availableLocales: availableLocales.value,
  ogType: 'article'
});

useHead(() => {
  if (!article.value) {
    return { script: [] };
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.value.meta.title,
    description: article.value.meta.description,
    datePublished: article.value.meta.date,
    inLanguage: currentLocale.value === 'en' ? 'en-US' : 'zh-CN',
    mainEntityOfPage: canonicalUrl.value
  };

  return {
    script: [
      {
        type: 'application/ld+json',
        textContent: JSON.stringify(schema)
      }
    ]
  };
});
</script>

<style scoped lang="scss">
.blog-detail-page {
  min-height: 100vh;
  background: $gray-50;
}

.hero {
  --hero-bg-image: none;
  --hero-gradient-from: #172554;
  --hero-gradient-mid: #1e3a8a;
  --hero-gradient-to: #1d4ed8;
  --hero-glow-a: rgba(255, 187, 94, 0.35);
  --hero-glow-b: rgba(255, 107, 61, 0.2);
  --hero-tag-bg: rgba(15, 23, 42, 0.26);
  --hero-tag-border: rgba(255, 255, 255, 0.24);

  position: relative;
  overflow: hidden;
  padding: $spacing-xxl 0 $spacing-xl;
  background-image:
    linear-gradient(130deg, rgba(15, 23, 42, 0.66) 0%, rgba(30, 58, 138, 0.48) 45%, rgba(29, 78, 216, 0.42) 100%),
    radial-gradient(circle at 85% 18%, var(--hero-glow-a), transparent 52%),
    radial-gradient(circle at 12% 78%, var(--hero-glow-b), transparent 48%),
    var(--hero-bg-image),
    linear-gradient(130deg, var(--hero-gradient-from) 0%, var(--hero-gradient-mid) 45%, var(--hero-gradient-to) 100%);
  background-position: center, center, center, center, center;
  background-size: auto, auto, auto, cover, auto;
  background-repeat: no-repeat;
  color: $white;
}

.hero-kicker {
  display: inline-flex;
  align-items: center;
  gap: $spacing-sm;
  margin-bottom: $spacing-md;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.12);
  font-size: 13px;
}

.hero-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
}

.hero-title {
  margin: 0 0 $spacing-md;
  max-width: 860px;
  font-size: 2.2rem;
  line-height: 1.2;
  letter-spacing: 0.01em;
}

.hero-description {
  margin: 0;
  max-width: 760px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.92);
}

.hero-tags {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing-sm;
  margin-top: $spacing-lg;
}

.hero-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: $border-radius-sm;
  font-size: 12px;
  background: var(--hero-tag-bg);
  border: 1px solid var(--hero-tag-border);
}

.container {
  max-width: 860px;
  margin: 0 auto;
  padding: 0 $spacing-lg;
}

.article-area {
  position: relative;
  z-index: 1;
}

.article-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 $spacing-lg;
}

.article-layout {
  display: block;
}

.article-main {
  min-width: 0;
}

.article-layout:not(.has-toc) .article-main {
  max-width: 860px;
  margin: 0 auto;
}

.article-shell {
  background: $white;
  border: 1px solid $gray-200;
  border-radius: $border-radius-md;
  padding: $spacing-xl;
}

.body {
  margin: 0;
  color: $gray-800;
  line-height: 1.8;

  :deep(p) {
    margin: 0 0 $spacing-md;
  }

  :deep(h2) {
    margin: $spacing-lg 0 $spacing-sm;
    scroll-margin-top: calc(60px + #{$spacing-md});
  }

  :deep(ul),
  :deep(ol) {
    padding-left: $spacing-xl;
  }
}

@media (min-width: 1200px) {
  .article-layout.has-toc {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    align-items: start;
    gap: $spacing-xl;
  }
}

@media (max-width: 1199px) {
  .article-container {
    max-width: 860px;
  }
}

@media (max-width: 768px) {
  .hero {
    padding: $spacing-xl 0 $spacing-lg;
    background-size: auto, auto, auto, contain, auto;
  }

  .hero-title {
    font-size: 1.7rem;
  }

  .hero-kicker {
    font-size: 12px;
  }

  .container,
  .article-container {
    padding: 0 $spacing-md;
  }

  .article-shell {
    padding: $spacing-lg;
  }
}
</style>
